---
title: "Stop Loss and Take Profit: Your First Safety Net"
slug: stop-loss-take-profit
shortTitle: "Stop Loss & Take Profit"
description: "How stop loss and take profit orders close a trade for you, where traders place them, how to size a position from your stop, and why a stop can still slip in fast markets."
level: beginner
category: trading
course: beginner
order: 8
topics: [risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: true
cover: /academy/img/covers/stop-loss-take-profit.svg
faqs:
  - q: "What is the difference between a stop loss and a take profit?"
    a: "A stop loss closes your position automatically to limit a loss if the price moves against you. A take profit closes it automatically to lock in a gain once the price reaches your target."
  - q: "Does a stop loss always close my trade at exactly my price?"
    a: "No. Once your stop price is reached, the position closes at the next available market price. In fast markets or when a price gaps, that can be worse than your stop level, which is called slippage."
  - q: "Can I add or change a stop loss after opening a trade?"
    a: "Yes. Use Edit TP / SL next to the position in Open Positions to set, change or remove either level. Leaving a field empty removes it. On desktop you can also drag the TP and SL lines on the chart."
  - q: "How far away should my stop loss be?"
    a: "There is no single right distance. Many traders place it where their trade idea is clearly wrong, leave room for normal price swings and the spread, and then choose a position size so that the loss at the stop is an amount they can accept."
  - q: "Does the platform offer trailing stops?"
    a: "Automatic trailing stops are not currently offered. You can trail a stop yourself by moving it with Edit TP / SL, or by dragging the SL line on the desktop chart, as the price moves in your favor."
---

Every trade can go wrong, and markets do not wait for you to be watching. A **stop loss** closes your position automatically if the price moves against you by an amount you chose in advance. A **take profit** closes it automatically when the price reaches your target. Together they turn a trade from an open-ended bet into a plan with a defined exit on both sides.

This lesson explains how both orders work, how they are triggered on {{BRAND}}, where traders tend to place them, how to size a position from your stop, and the limits every trader should understand.

<CTA />

<KeyTakeaways>

- **A stop loss caps the planned loss.** It closes the position at market when the price reaches your stop level, so a bad trade stays a small one.
- **A take profit banks a planned gain.** It closes the position once the price reaches your target, so you are not relying on watching the screen.
- **Place the stop first, then size the trade.** Put the stop where your idea is wrong, then choose a size so the loss at that level is one you can accept.
- **Stops can slip.** In fast markets and gaps a stop fills at the next available price, which can be worse than your level.

</KeyTakeaways>

## What stop loss and take profit orders do

Both are instructions attached to a position that close it for you:

- A **stop loss (SL)** sits on the losing side of your entry. For a buy it goes **below** the entry; for a sell it goes **above**.
- A **take profit (TP)** sits on the winning side. For a buy it goes **above** the entry; for a sell it goes **below**.

When either level is reached, the position closes and the result is added to or taken from your balance. In the **Closed Positions** tab, the **Reason** column then shows **Stop loss** or **Take profit**, so you can see exactly how each trade ended.

## How they are triggered on {{BRAND}}

Stops and targets are checked against the price you would **close** at. A buy closes at the Bid, so its TP and SL watch the **Bid**. A sell closes at the Ask, so its TP and SL watch the **Ask**. If you are not sure why that matters, [Bid, Ask, Spread and Why They Matter](/academy/article/bid-ask-spread) shows how a widening spread can trigger a stop early.

<CompareTable caption="Stop loss and take profit compared">

| | Stop loss | Take profit |
| --- | --- | --- |
| Job | Limits a loss | Locks in a gain |
| Placed for a buy | Below the entry | Above the entry |
| Placed for a sell | Above the entry | Below the entry |
| Checked against | Bid for a buy, Ask for a sell | Bid for a buy, Ask for a sell |
| Fills at | Market, so it can slip | Your level or better |
| Reason in Closed Positions | Stop loss | Take profit |

</CompareTable>

## Setting TP / SL on {{BRAND}}

You can attach both levels before the trade opens or add them afterwards.

<Steps>
<Step title="Turn on TP / SL in the order panel">

On the trade screen, after choosing **Buy** or **Sell** and a size, switch on **TP / SL**.

</Step>
<Step title="Enter a price or an amount">

Type a **TP Price** and **SL Price**, or switch to a **Profit (USDT)** and **Loss (USDT)** amount and let the panel work out the price. Either way it shows the percentage distance from your entry.

</Step>
<Step title="Check the numbers, then confirm">

Read the **Loss (USDT)** figure. It is the amount you stand to lose if the stop is hit at your level, before fees and any slippage. If it is too large, reduce the size.

</Step>
<Step title="Adjust it later if you need to">

In **Open Positions**, use **Edit TP / SL** to change or add a level. Clearing a field removes that level. On desktop you can also drag the TP and SL lines on the chart and confirm the change.

</Step>
</Steps>

<Screenshot name="order-panel-tp-sl" alt="The TP / SL section of the order panel with price and USDT amount fields" caption="TP / SL in the order panel: set a price or a USDT amount, and see the distance from your entry." />

## Where to place a stop loss

A useful stop answers one question: **at what price is my reason for this trade clearly wrong?** Three ideas help:

1. **Use the chart, not a round number.** If you bought because the price was holding above a support level, your idea is wrong if it breaks clearly below it. Put the stop a little beyond that level, not exactly on it.
2. **Leave room for normal noise.** Every market wobbles. A stop closer than the usual back-and-forth will often be hit before the trade has a chance to work. Remember to allow for the spread too.
3. **Never let the stop be decided by how much you hope to lose.** Choose the level first, then adjust the size.

## Sizing a trade from your stop

This is the habit that does most to protect an account. Decide how much of your balance you are willing to lose on one trade, often a small percentage, then work backwards.

<Example title="A gold trade sized from its stop (illustrative prices)">

Your balance is 2,000 USDT and you decide to risk 1% on this trade: 2,000 × 1% = **20 USDT**.

You plan to buy GOLD/USDT at 2,400 with a stop at 2,380. The distance is 2,400 − 2,380 = **20 points**.

Size = risk ÷ distance = 20 ÷ 20 = **1 ounce**, which is 0.01 lots (1 lot of gold is 100 ounces).

You set a take profit at 2,440: (2,440 − 2,400) × 1 = **+40 USDT**. You are risking 20 to aim for 40, a reward-to-risk ratio of 2 to 1.

</Example>

The same works for a sell. If you sell 0.5 ETH at 3,000 with a stop at 3,060 and a target at 2,880, the planned loss is (3,060 − 3,000) × 0.5 = **30 USDT** and the planned gain is (3,000 − 2,880) × 0.5 = **60 USDT**. [Position Sizing](/academy/article/position-sizing) and [Risk/Reward Ratio and Win Rate](/academy/article/risk-reward-and-win-rate) take this further.

<ProTip>

Use the **Loss (USDT)** field as a check, not as the place to start. Pick your stop price from the chart first, then change the size until the Loss (USDT) figure matches the amount you decided to risk.

</ProTip>

## Where to place a take profit

A take profit is a planned exit for a winning trade. Common approaches are to place it just before the next level where the price has turned in the past, or at a fixed multiple of your stop distance, such as twice as far. A target that is very far away may rarely be reached. One that is very close may not cover your losses on the trades that hit their stops.

Some traders prefer to take part of the profit early. On desktop, **Instant Trade** has 25% and 50% close buttons for that; the regular **Close** button closes the whole position.

## Moving your stop as the trade works

Once a trade is well in profit, some traders move the stop closer, for example to the entry price, so the trade can no longer turn into a loss beyond costs and slippage. Doing this step by step as the price moves is called **trailing** a stop.

{{BRAND}} does not offer an automatic trailing stop. You can trail manually by updating the SL with **Edit TP / SL**, or by dragging the SL line on the desktop chart. [Setting SL/TP and Trailing Stops on {{BRAND}}](/academy/article/setting-tp-sl-trailing-stops) shows how.

{/* TODO: verify with platform — automatic trailing stops are not currently offered. */}

The one move to avoid is the opposite: pushing a stop further away because the price is getting close to it. That turns a planned loss into an unplanned, larger one.

## What a stop loss cannot do

A stop loss is a safety net, not a wall. Once triggered, it closes at the next available market price.

<Example title="A gap through a stop (illustrative prices)">

You buy 10 shares of a stock CFD at 180 with a stop at 174. Your planned loss is (174 − 180) × 10 = **−60 USDT**.

After disappointing results, the market reopens at 168. Your stop is triggered at the open and fills near 168: (168 − 180) × 10 = **−120 USDT**, twice the planned loss.

</Example>

<Warning>

Stops can slip during news, at market opens and in fast moves, and the fill can be well beyond your stop level. Leverage makes that slippage larger relative to your margin. Keep positions small enough that a slipped stop is still a loss you can absorb.

</Warning>

## Conclusion

A stop loss closes a losing trade at a level you chose in advance, and a take profit closes a winning one at your target. On {{BRAND}} you set both in the order panel with **TP / SL**, as a price or a USDT amount, and adjust them later with **Edit TP / SL**. Place the stop where your idea is wrong, size the position so the loss there is acceptable, and remember that a stop fills at market and can slip. Used on every trade, these two orders are the simplest way to keep any single mistake small.
